// @flow
import * as React from 'react';
import Box from 'sail/Box';
import {Table, TableCell, TableHeaderCell} from '../../core/Table/Table';

const DATA = {
  typography: [
    {
      token: 'heading',
      usage: 'Used for labeling a section of your app',
      styles: {
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 700,
      },
    },
    {
      token: 'subheading',
      usage: 'Used for labeling content within a section of your app',
      styles: {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 600,
      },
    },
    {
      token: 'body',
      usage: 'Primary body text of the app',
      styles: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 400,
      },
    },
    {
      token: 'caption',
      usage: 'Used for text that should be less prominent than body text',
      styles: {
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 400,
      },
    },
  ],
  colors: {
    background: [
      {
        token: 'surface',
        usage: 'Used for the background of apps',
        value: '#fff',
      },
      {
        token: 'container',
        usage: 'Used for cards and sections within an app',
        value: '#E0E6EB',
      },
    ],
    border: [
      {
        token: 'neutral',
        usage: 'The default color for all borders and dividers',
        value: '#E0E6EB',
      },
    ],
    text: [
      {
        token: 'primary',
        usage: 'The default color for text and icons',
        value: '#30313D',
      },
      {
        token: 'secondary',
        usage: 'Used to for text and icons which are less prominent',
        value: '#6A7383',
      },
      {
        token: 'disabled',
        usage: 'Used for elements which are disabled',
        value: '#A3ACBA',
      },
      {
        token: 'info',
        usage: 'Used for content that is neutral and informational',
        value: '#0570DE',
      },
      {
        token: 'success',
        usage: 'Used for content which indicates the success of some action',
        value: '#228403',
      },
      {
        token: 'attention',
        usage: 'Used for content which is should grab the user’s attention',
        value: '#C84801',
      },
      {
        token: 'critical',
        usage:
          'Used for content which is critically urgent to the user. Should be used sparingly.',
        value: '#DF1B41',
      },
    ],
  },
};

const Swatch = ({color}) => (
  <div
    style={{
      background: color,
      height: '32px',
      width: '32px',
      borderRadius: '4px',
      boxShadow: 'inset 0 0 1px #A3ACBA',
    }}
  />
);

const TypeExample = ({styles}) => (
  <div style={styles}>The quick brown fox jumps over the lazy dog.</div>
);

const ColorTable = ({category}: {category: string}) => {
  const items = DATA.colors[category];

  return (
    <Table fixed type="striped">
      <thead>
        <tr>
          <TableHeaderCell>Example</TableHeaderCell>
          <TableHeaderCell>Token</TableHeaderCell>
          <TableHeaderCell>Usage</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.token}>
            <TableCell>
              <Swatch color={item.value} />
            </TableCell>
            <TableCell>
              <code className="InlineCode" style={{userSelect: 'all'}}>
                {item.token}
              </code>
            </TableCell>
            <TableCell>{item.usage}</TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const TypographyTable = () => {
  const items = DATA.typography;

  return (
    <Table fixed type="striped">
      <thead>
        <tr>
          <TableHeaderCell>Token</TableHeaderCell>
          <TableHeaderCell>Usage</TableHeaderCell>
          <TableHeaderCell>Example</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.token}>
            <TableCell>
              <code className="InlineCode" style={{userSelect: 'all'}}>
                {item.token}
              </code>
            </TableCell>
            <TableCell>{item.usage}</TableCell>
            <TableCell>
              <TypeExample styles={item.styles} />
            </TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

type TailorFrameworkExampleProps = {|
  children: React.Node,
  type: string,
  args: any,
|};

export const TailorFrameworkExample = ({
  type,
  args,
  children,
}: TailorFrameworkExampleProps) => {
  if (type === 'layout') {
    if (args.direction === 'row') {
      return (
        <div style={{marginTop: '16px', display: 'flex', gap: '16px'}}>
          <Box background="gray100" padding={20} />
          <Box background="gray100" padding={20} />
          <Box background="gray100" padding={20} />
        </div>
      );
    } else {
      return (
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box style={{width: 20}} background="gray100" padding={20} />
          <Box style={{width: 20}} background="gray100" padding={20} />
          <Box style={{width: 20}} background="gray100" padding={20} />
        </div>
      );
    }
  }

  if (type === 'color') {
    return <ColorTable category={args.category} />;
  }

  if (type === 'typography') {
    return <TypographyTable />;
  }

  return null;
};
